import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Sidebar from '@/shared/components/Sidebar'
import Header from '@/shared/components/Header'
import type { UserProfile } from '@/services/api'
import { rutaActivaDeUrl } from './rutaActiva'

/**
 * Marco de las pantallas autenticadas: barra lateral, encabezado y el contenido
 * de la ruta.
 *
 * Sustituye al componente `Dashboard` de App.tsx, que hacía de router a mano:
 * guardaba la pantalla activa en un `useState`, la sincronizaba con la URL
 * dentro de un `useEffect` y elegía qué pintar con un `switch`. Eso provocaba un
 * primer render con la pantalla anterior y hacía que la URL dejara de ser la
 * única fuente de verdad.
 *
 * Aquí no hay estado de navegación: la ruta la resuelve React Router y el
 * contenido entra por `<Outlet/>`.
 */

interface DashboardLayoutProps {
  user: UserProfile | null
  onLogout: () => void
}

const DashboardLayout = ({ user, onLogout }: DashboardLayoutProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true)

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        activeRoute={rutaActivaDeUrl(pathname)}
        onNavigate={(route: string) => navigate(`/${route}`)}
        user={user}
        onCollapsedChange={setIsSidebarCollapsed}
      />

      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-20' : 'ml-80'
        }`}
      >
        <Header onLogout={onLogout} user={user} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
